import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import { SLAConfigMetaDataResponse } from '../@types';
import { SLADateUpdate } from '../@types/sla.type';
import {
  getSLAConfigMetaData,
  getSLAsByServiceId,
  refreshSLAsByServiceId,
  resetSLAById,
  resetSLAsByServiceId,
  updateSLATimeoutAt,
} from '../api/sla';
import { SLAContactRowData } from '../components/organisms/SLAContactRow/SLAContactRow.props';
import { SLAConfigMetaDataKey, SLAsByServiceKey } from '../configs/sla';

export const useGetSLAsByServiceId = (serviceId: number): UseQueryResult<SLAContactRowData[]> => {
  return useQuery({
    queryKey: [SLAsByServiceKey, serviceId],
    queryFn: () => getSLAsByServiceId(serviceId),
  });
};

export const useUpdateSLATimeoutAt = (
  serviceId: number
): UseMutationResult<void, Error, SLADateUpdate & { id: number }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SLADateUpdate & { id: number }) => {
      return await updateSLATimeoutAt(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SLAsByServiceKey, serviceId],
      });
    },
  });
};

export const useResetSLAsByServiceId = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await resetSLAsByServiceId(serviceId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SLAsByServiceKey, serviceId],
      });
    },
  });
};

export const useResetSLAById = (
  serviceId: number
): UseMutationResult<void, Error, { slaId: number }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { slaId: number }) => {
      return await resetSLAById(data.slaId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SLAsByServiceKey, serviceId],
      });
    },
  });
};

export const useRefreshSLAsByServiceId = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await refreshSLAsByServiceId(serviceId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SLAsByServiceKey, serviceId],
      });
    },
  });
};

export const useGetSLAConfigMetaData = (): UseQueryResult<SLAConfigMetaDataResponse> => {
  return useQuery({
    queryKey: [SLAConfigMetaDataKey],
    queryFn: () => getSLAConfigMetaData(),
    staleTime: 5 * 60 * 60 * 1000,
  });
};
